/**
 * ==============================================================================
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 * ==============================================================================
 * Statik şablon: `apps/web/src/locales/*` uydu/projede doldurulur; DLL bu sağlayıcı mantığını taşır.
 */

import React, { createContext, useState, useEffect, useSyncExternalStore } from 'react';
import axios from '../api/axios';
import { DEFAULT_LANGUAGE, normalizeLanguage } from '../utils/language.utils';
import { useAuth } from './AuthContext';
import { readCapturePresentation } from '../config/capture-presentation.config';
import {
    COMPANY_SCOPE_HEADER,
    getValidatedSelectedCompanyIdForRequest,
    subscribeValidatedSelectedCompanyIdForRequest,
} from '../config/company-scope.config';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { ar } from '../locales/ar';
import { de } from '../locales/de';
import { fr } from '../locales/fr';
import { es } from '../locales/es';
import { it } from '../locales/it';
import { ru } from '../locales/ru';
import { zh } from '../locales/zh';
import { ja } from '../locales/ja';
import { ModalLabelsProvider } from '../components/shared/ModalLabels';

type Translations = Record<string, any>;

const locales: Record<string, Translations> = {
    tr, en, ar, de, fr, es, it, ru, zh, ja
};

const TRANSLATION_CONTEXT_GLOBAL_KEY = 'qubepro_translation_context';

function getStableTranslationContext(): React.Context<Translations> {
    const g = globalThis as typeof globalThis & {
        [TRANSLATION_CONTEXT_GLOBAL_KEY]?: React.Context<Translations>;
    };
    if (!g[TRANSLATION_CONTEXT_GLOBAL_KEY]) {
        g[TRANSLATION_CONTEXT_GLOBAL_KEY] = createContext<Translations>(locales[DEFAULT_LANGUAGE]);
    }
    return g[TRANSLATION_CONTEXT_GLOBAL_KEY]!;
}

export const TranslationContext = getStableTranslationContext();

/** Public surfaces must not initialize tenant auth, company overlays or an
 * authenticated translation request just to render shared controls. */
export function StaticTranslationProvider({ language, children }: { language: string; children: React.ReactNode }) {
    const activeLanguage = normalizeLanguage(language);
    const translations = locales[activeLanguage] || locales[DEFAULT_LANGUAGE];
    return <TranslationContext.Provider value={translations}>
        <ModalLabelsProvider labels={translations.common}>{children}</ModalLabelsProvider>
    </TranslationContext.Provider>;
}

export function TranslationProvider({ children }: { children: React.ReactNode }) {
    const { user, language, isInitialized, isAuthenticated } = useAuth();
    // TranslationProvider also wraps public pages and sits above CompanyProvider.
    // Subscribe to its validated request scope; storage/URL values are not proof.
    const companyId = useSyncExternalStore(
        subscribeValidatedSelectedCompanyIdForRequest,
        getValidatedSelectedCompanyIdForRequest,
        () => null,
    );
    const activeLanguage = normalizeLanguage(language);
    const overlayKey = isInitialized && isAuthenticated && user?.tenantId && companyId !== null
        ? `${user.tenantId}:${user.id}:${activeLanguage}:${companyId}` : null;
    const [overlay, setOverlay] = useState<{ key: string; translations: Translations } | null>(null);
    const activeTranslations = overlayKey && overlay?.key === overlayKey
        ? overlay.translations : locales[activeLanguage] || locales[DEFAULT_LANGUAGE];

    // RTL — Arapça için yön üst bileşenden
    useEffect(() => {
        const dir = activeLanguage === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.dir = dir;
        document.documentElement.lang = activeLanguage;
        const presentation = readCapturePresentation();
        if (presentation && (presentation.mode === 'SITE_DEFAULT' || presentation.locale === activeLanguage)) {
            document.documentElement.dataset.qpCapturePresentation = presentation.contractId;
            document.documentElement.dataset.qpCaptureLocale = activeLanguage;
            document.documentElement.dataset.qpCaptureMode = presentation.mode;
        } else {
            delete document.documentElement.dataset.qpCapturePresentation;
            delete document.documentElement.dataset.qpCaptureLocale;
            delete document.documentElement.dataset.qpCaptureMode;
        }
        return () => {
            delete document.documentElement.dataset.qpCapturePresentation;
            delete document.documentElement.dataset.qpCaptureLocale;
            delete document.documentElement.dataset.qpCaptureMode;
        };
    }, [activeLanguage]);

    useEffect(() => {
        setOverlay(null);
        if (!overlayKey) {
            return;
        }

        // Kiracılı dinamik çeviriler: dil veya tenant değişiminde eksik tepki / üst üste yanıt uygulanmasın
        let cancelled = false;

        axios
            .get('/api/translations', {
                headers: { [COMPANY_SCOPE_HEADER]: companyId },
                params: {
                    locale: activeLanguage,
                    size: 10000,
                },
            })
            .then((res) => {
                if (cancelled) return;
                if (res.data.success && Array.isArray(res.data.data.items)) {
                    const dynamicItems = res.data.data.items;
                    const baseClone = locales[activeLanguage] || locales[DEFAULT_LANGUAGE];
                    const newT = JSON.parse(JSON.stringify(baseClone));

                    dynamicItems.forEach((item: any) => {
                        if (normalizeLanguage(item?.locale) !== activeLanguage) {
                            return;
                        }
                        if (item.entityType && item.fieldName) {
                            if (!newT[item.entityType]) {
                                newT[item.entityType] = {};
                            }
                            if (newT[item.entityType]) {
                                newT[item.entityType][item.fieldName] = item.value;
                            }
                        }
                    });

                    setOverlay({ key: overlayKey, translations: newT });
                } else {
                    setOverlay(null);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                if (import.meta.env.DEV) console.error('[TranslationProvider] Failed to fetch translations:', err);
                setOverlay(null);
            });

        return () => {
            cancelled = true;
        };
    }, [overlayKey, activeLanguage, companyId]);

    return (
        <TranslationContext.Provider value={activeTranslations}>
            <ModalLabelsProvider labels={activeTranslations.common}>{children}</ModalLabelsProvider>
        </TranslationContext.Provider>
    );
}
